import * as angular from "angular";
import { IOnChangesObject } from "angular";
import "./geofenceOverlay.scss";
import { IGeoArea } from "src/app/main/modules/tms/_models/IGeoArea";
import { IVeriMapChildComponent, IVeriMapComponent } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";
import { GeoAreaOverlay } from "@directives/_map/geofences/GeoareaOverlay";
import { Debounce } from "@services/debounce.service";
import { GeofenceService } from "@services/geofence.service";
import { GeofenceType } from "@enums/GeofenceType";

export interface IGeoAreaDirectiveScope extends angular.IScope {
    geoareas: IGeoArea[];
}

interface IGeoAreaPolygonGroup {
    geoarea: IGeoArea;
    polygons: google.maps.Polygon[];
}

export class GeoAreaComponentController implements angular.IComponentController, IVeriMapChildComponent {
    // map
    veriMap: IVeriMapComponent;
    map: google.maps.Map;
    mapEventListeners: google.maps.MapsEventListener[] = [];

    geoAreaOverlays: GeoAreaOverlay[] = [];

    //
    geoareas: IGeoArea[];
    previousBounds: google.maps.LatLngBounds;

    //private lastMouseLatLng: google.maps.LatLng;
    private debouncedToggleVisibility: () => void;
    private loadingGeofences = false;

    static $inject = ["$scope", "geofenceService", "debounceService"];

    constructor(
        private readonly $scope: angular.IScope,
        private readonly geofenceService: GeofenceService,
        private readonly debounceService: Debounce) {
    }

    $onInit() {
        this.veriMap.registerChildComponent(this);

        this.veriMap.mapInitialisedPromise.then(map => {
            this.setMap(map);
        });

        this.debouncedToggleVisibility = this.debounceService.debounce(() => { this.toggleViewportGeoAreas(); }, 100, false);
    }

    $onChanges(onChangesObj: IOnChangesObject): void {
        if (onChangesObj["hideGeofence"]) {
            this.toggleViewportGeoAreas();
        }

        if (onChangesObj["geoareas"] && this.geoareas) {
            if (!this.map) {
                this.veriMap.mapInitialisedPromise.then(map => {
                    this.setMap(map);
                    this.createGeofencesOverlays();
                });
            }
            else {
                this.createGeofencesOverlays();
            }
        }
    }

    $onDestroy() {
        this.mapEventListeners.forEach(listener => {
            google.maps.event.removeListener(listener);
        });

        this.destroyOverlays();

        this.veriMap.deregisterChildComponent(this);
    }

    setMap(map: google.maps.Map) {
        if (this.map) {
            return;
        }

        this.map = map;

        const zoomListener = this.map.addListener("zoom_changed", () => {
            this.debouncedToggleVisibility();
        });

        const idleListener = this.map.addListener("idle", () => {
            this.debouncedToggleVisibility();
        });

        const mapTypeListener = this.map.addListener("maptypeid_changed", () => {
            this.geoAreaOverlays.forEach(go => {
                go.updateColoursForMapType();
                go.drawGeofence(true);
            });
        });

        this.mapEventListeners.push(mapTypeListener);
        this.mapEventListeners.push(idleListener);
        this.mapEventListeners.push(zoomListener);
    }

    onMouseMove(mousePosition: google.maps.LatLng, event: Event): boolean {

        let closestOverlayMatch: GeoAreaOverlay = null;
        const closestOverlayMatchDistance = 0;

        for (let i = 0; i < this.geoAreaOverlays.length; i++) {
            const overlay = this.geoAreaOverlays[i];

            if (!overlay || !overlay.overlay || !overlay.visible) {
                continue;
            }

            // Reset hover status
            overlay.setHover(false);

            // Note this works on a square so isnt 100% accurate for polygons and edges of circles
            if (overlay.bounds.contains(mousePosition) && this.geofenceService.pointIsInsideGeofence(mousePosition, overlay.geoArea)) {

                const distanceFromPointToGeofence = google.maps.geometry.spherical.computeDistanceBetween(mousePosition, overlay.geoArea.MapCoords);

                if (closestOverlayMatch == null || distanceFromPointToGeofence < closestOverlayMatchDistance) {

                    closestOverlayMatch = overlay;
                }
            }
        }

        if (closestOverlayMatch) {
            this.highlightGeofenceOverlay(closestOverlayMatch);
        }

        return false;
    }

    private highlightGeofenceOverlay(overlay: GeoAreaOverlay, event?: google.maps.MouseEvent): void {

        if (!overlay.visible) {
            return;
        }

        overlay.setHover(true);

        this.$scope.$apply();
    }

    // -------------------------------------------------

    private createGeofencesOverlays() {
        this.destroyOverlays();

        this.geoAreaOverlays = this.createGeoAreaOverlays();
        this.toggleGeoareas(this.map.getBounds());
    }

    createGeoAreaOverlays() {
        window.performance.mark("createGeofenceOverlays");
        const overlays: GeoAreaOverlay[] = [];

        if (!this.geoareas) {
            return overlays;
        }

        let i = this.geoareas.length;
        while (i--) {
            const geofence = this.geoareas[i];
            const overlay = this.createGeoAreaOverlay(geofence, this.map);

            if (overlay) {
                overlays.push(overlay);
            }
        }
        window.performance.measure("createGeofenceOverlays");

        return overlays;
    }

    createGeoAreaOverlay(geoArea: IGeoArea, map: google.maps.Map): GeoAreaOverlay {

        if (!geoArea) {
            return null;
        }

        if (geoArea.GeofenceType == GeofenceType.Circle) {
        }
        else if (geoArea.GeofenceType == GeofenceType.Polygon) {
            const coords = [];

            if (!geoArea.Points) {
                //console.error("Geofence set to polygon but has no points", geofence);

                return null;
            }

            for (let i = 0; i < geoArea.Points.length; i++) {
                const [lat, lng] = geoArea.Points[i];
                coords.push({ lat, lng });
            }
        }
        else {
            return null;
        }

        this.createGeoAreaCoords(geoArea);

        return new GeoAreaOverlay(geoArea, map, true, true);
    }

    createGeoAreaCoords(geoArea: IGeoArea) {
        if (!geoArea.MapCoords) {
            geoArea.MapCoords = new google.maps.LatLng(geoArea.Latitude, geoArea.Longitude);
        }
    }

    private toggleViewportGeoAreas() {

        if (!this.map) {
            return;
        }

        const bounds = this.map.getBounds();

        if (this.previousBounds != null && this.previousBounds === bounds) {
            // If our viewport hasnt changed don't re-render
            return;
        }

        this.toggleGeoareas(bounds);
    }

    private toggleGeoareas(bounds: google.maps.LatLngBounds) {
        if (this.loadingGeofences) {
            return;
        }

        this.previousBounds = bounds;
        const zoomLevel = this.map.getZoom();
        const visibleForZoomLevel = zoomLevel > 10;

        for (let i = 0; i < this.geoAreaOverlays.length; i++) {
            const overlay = this.geoAreaOverlays[i];

            if (!overlay || !overlay.geoArea) {
                continue;
            }

            if (visibleForZoomLevel && bounds.intersects(overlay.bounds)) {

                overlay.showElement();
                overlay.setHover(false);
            }
            else {
                overlay.hideElement();
            }
        }
    }

    private destroyOverlays() {
        this.geoAreaOverlays.forEach((overlay) => {
            overlay.hideElement();
            overlay.setHover(false);

            if (overlay && overlay.overlay) {
                overlay.overlay.setMap(null);
            }
        });
    }
}

export class GeoAreasComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    require: { [index: string]: string; };

    constructor() {
        this.bindings = {
            geoareas: "<",
        };

        this.require = {
            veriMap: "^veriMap"
        };

        this.controller = GeoAreaComponentController;
    }
}
